import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { logAction, SECTIONS, ACTIONS } from '../services/auditLog';
import { PANEL_ROUTE_OPTIONS, getLegacyRoutesForRole } from '../config/panelRoutes';
import { hashPassword } from '../utils/passwordHash';
import './UserManagement.css';

const ADMIN_USERS_LIST_COLUMNS =
  'id, email, name, role, is_active, panel_allowed_routes, last_login, created_at';

const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Pełny dostęp do wszystkich funkcji' },
  { value: 'moderator', label: 'Moderator', description: 'Dostęp do aktualności, wydarzeń i atrakcji' },
  { value: 'support', label: 'Obsługa', description: 'Tylko przeglądanie zgłoszeń i wysyłanie prośb' },
  { value: 'secretary', label: 'Sekretarz', description: 'Powiadomienia, dziennik zdarzeń, zgłoszenia (zmiana statusu)' },
];

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'moderator',
    is_active: true,
    allowedPaths: getLegacyRoutesForRole('moderator'),
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('admin_users')
        .select(ADMIN_USERS_LIST_COLUMNS)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Błąd podczas ładowania użytkowników');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.name) {
      toast.error('Proszę wypełnić wszystkie wymagane pola');
      return;
    }

    // Dla nowych użytkowników wymagane jest hasło
    if (!editingUser && !formData.password) {
      toast.error('Hasło jest wymagane dla nowych użytkowników');
      return;
    }

    if (formData.role !== 'admin' && (!formData.allowedPaths || formData.allowedPaths.length === 0)) {
      toast.error('Wybierz co najmniej jedną zakładkę panelu');
      return;
    }

    const panelAllowed =
      formData.role === 'admin' ? null : [...formData.allowedPaths];

    try {
      if (editingUser) {
        // Aktualizacja istniejącego użytkownika
        const updateData = {
          email: formData.email.toLowerCase().trim(),
          name: formData.name,
          role: formData.role,
          is_active: formData.is_active,
          panel_allowed_routes: panelAllowed,
        };

        // Aktualizuj hasło tylko jeśli zostało podane
        if (formData.password) {
          updateData.password_hash = await hashPassword(formData.password);
        }

        const { error } = await supabaseAdmin
          .from('admin_users')
          .update(updateData)
          .eq('id', editingUser.id);

        if (error) throw error;
        toast.success('Użytkownik zaktualizowany pomyślnie');
        await logAction(
          {
            section: SECTIONS.USERS,
            action: ACTIONS.UPDATE,
            entityType: 'admin_user',
            entityId: editingUser.id,
            entityLabel: formData.name || formData.email,
            details: `Zaktualizowano użytkownika: rola ${formData.role}, aktywny: ${formData.is_active}, zakładki: ${formData.role === 'admin' ? 'wszystkie' : (panelAllowed?.length ?? 0)}.`,
          },
          user
        );
      } else {
        // Tworzenie nowego użytkownika
        const { error } = await supabaseAdmin
          .from('admin_users')
          .insert({
            email: formData.email.toLowerCase().trim(),
            password_hash: await hashPassword(formData.password),
            name: formData.name,
            role: formData.role,
            is_active: formData.is_active,
            panel_allowed_routes: panelAllowed,
          });

        if (error) throw error;
        toast.success('Użytkownik utworzony pomyślnie');
        await logAction(
          {
            section: SECTIONS.USERS,
            action: ACTIONS.CREATE,
            entityType: 'admin_user',
            entityLabel: formData.name || formData.email,
            details: `Dodano użytkownika: ${formData.email}, rola: ${formData.role}, zakładki: ${formData.role === 'admin' ? 'wszystkie' : (panelAllowed?.length ?? 0)}.`,
          },
          user
        );
      }

      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.message || 'Błąd podczas zapisywania użytkownika');
    }
  };

  const handleEdit = (userRow) => {
    setEditingUser(userRow);
    const paths =
      Array.isArray(userRow.panel_allowed_routes)
        ? [...userRow.panel_allowed_routes]
        : getLegacyRoutesForRole(userRow.role || 'moderator');
    setFormData({
      email: userRow.email,
      password: '',
      name: userRow.name,
      role: userRow.role || 'admin',
      is_active: userRow.is_active,
      allowedPaths: paths,
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego użytkownika?')) {
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('admin_users')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      const deletedUser = users.find((u) => u.id === userId);
      toast.success('Użytkownik usunięty pomyślnie');
      await logAction(
        {
          section: SECTIONS.USERS,
          action: ACTIONS.DELETE,
          entityType: 'admin_user',
          entityId: userId,
          entityLabel: deletedUser?.name || deletedUser?.email,
          details: 'Usunięto użytkownika z panelu.',
        },
        user
      );
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Błąd podczas usuwania użytkownika');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'moderator',
      is_active: true,
      allowedPaths: getLegacyRoutesForRole('moderator'),
    });
  };

  const toggleAllowedPath = (path) => {
    setFormData((prev) => {
      const next = new Set(prev.allowedPaths);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return { ...prev, allowedPaths: [...next] };
    });
  };

  const getRoleLabel = (role) => {
    const roleObj = ROLES.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <h2>Zarządzanie użytkownikami</h2>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Dodaj użytkownika
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Imię i nazwisko</th>
              <th>Rola</th>
              <th>Status</th>
              <th>Ostatnie logowanie</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                  Brak użytkowników
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name || '-'}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </td>
                  <td>
                    {user.last_login
                      ? new Date(user.last_login).toLocaleString('pl-PL')
                      : 'Nigdy'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(user)}
                      >
                        Edytuj
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(user.id)}
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div
            className={`modal-content ${formData.role !== 'admin' ? 'modal-user-form-wide' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{editingUser ? 'Edytuj użytkownika' : 'Dodaj użytkownika'}</h3>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser} // Nie można zmieniać emailu
                />
              </div>

              <div className="form-group">
                <label className="form-label">Imię i nazwisko *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Rola *</label>
                <select
                  className="form-input"
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setFormData({
                      ...formData,
                      role: newRole,
                      allowedPaths:
                        newRole === 'admin'
                          ? []
                          : getLegacyRoutesForRole(newRole),
                    });
                  }}
                  required
                >
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </select>
              </div>

              {formData.role === 'admin' ? (
                <div className="form-group form-hint-block">
                  <p className="form-hint">
                    Administrator ma automatycznie pełny dostęp do wszystkich zakładek panelu.
                  </p>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Dostęp do zakładek *</label>
                  <p className="form-hint form-hint-tight">
                    Zaznaczone zakładki — pełne uprawnienia (jak administrator) tylko w tych sekcjach.
                  </p>
                  <div className="panel-routes-checkboxes">
                    {PANEL_ROUTE_OPTIONS.map((opt) => (
                      <label key={opt.path} className="form-checkbox-label panel-route-row">
                        <input
                          type="checkbox"
                          checked={formData.allowedPaths.includes(opt.path)}
                          onChange={() => toggleAllowedPath(opt.path)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Hasło {editingUser ? '(zostaw puste aby nie zmieniać)' : '*'}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Zostaw puste aby nie zmieniać' : 'Wprowadź hasło'}
                />
                <small className="form-hint">
                  Hasło jest zapisywane w bazie jako hash bcrypt (nie w postaci jawnej).
                </small>
              </div>

              <div className="form-group">
                <label className="form-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Konto aktywne</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Anuluj
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Zapisz zmiany' : 'Utwórz użytkownika'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
